import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: '遠方に住んでいても依頼できますか?',
    answer: 'はい、もちろん可能です。神奈川県内はもちろん、東京都・千葉県・埼玉県など関東圏全域に対応しております。お電話やメールでのやり取りで、お客様が現地にいらっしゃらなくても、ご実家の整理をスムーズに進めることができます。事前にご希望の日時をお伺いし、訪問後は写真付きの査定報告書をお送りいたしますので、安心してお任せください。'
  },
  {
    question: '実家の大型家具も対応できますか?',
    answer: 'はい、大型家具も喜んで対応いたします。タンス、食器棚、ソファ、ベッドなど、重くて運び出しが困難な家具も、私たちが責任を持って搬出・買取または処分いたします。2階からの搬出や狭い通路での作業も経験豊富なスタッフが対応いたしますので、ご安心ください。事前に写真をお送りいただければ、おおよその見積もりもお出しできます。'
  },
  {
    question: '品物の価値がわからなくても大丈夫ですか?',
    answer: 'まったく問題ございません。むしろ、そのような方こそ私たちにお任せください。10年の経験を持つベテラン査定士・吉田が、一点一点丁寧に査定し、品物の価値を正確に見極めます。骨董品、着物、貴金属、ブランド品など、幅広いジャンルに精通しておりますので、「これは価値があるのかな?」と思われる品物がございましたら、お気軽にご相談ください。思わぬ高値がつくこともございます。'
  },
  {
    question: '査定後に断ることはできますか?',
    answer: 'もちろん可能です。査定は完全無料で、査定後にご納得いただけない場合は、遠慮なくお断りください。強引な営業や無理な勧誘は一切いたしません。お客様が心から納得された上でのお取引を大切にしておりますので、「やっぱり売らない」というご判断も尊重いたします。出張費・査定費も一切かかりませんので、安心してご依頼ください。'
  },
  {
    question: '複数回に分けて依頼できますか?',
    answer: 'はい、複数回に分けてのご依頼も大歓迎です。ご実家の整理は一度にすべて終わらせる必要はありません。「まずは貴重品だけ」「次回は家具類」といったように、段階的に進めることも可能です。お客様のペースに合わせて柔軟に対応いたしますので、無理のないスケジュールでご実家の整理を進めていただけます。2回目以降のご依頼も、もちろん出張費無料です。'
  }
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 bg-[#F5F5F5]">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#00A58E] text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
            よくあるご質問
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            お客様からよくいただくご質問
          </h2>
          <p className="text-lg text-gray-600">
            ご実家の整理や遠方からのご依頼について、よくあるご質問にお答えします
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-md overflow-hidden transition-all hover:shadow-lg"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-4 flex-1">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#00A58E] to-[#008C7A] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    Q{index + 1}
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-[#3E2723] pt-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {faq.question}
                  </h3>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-[#00A58E] transition-transform w-6 h-6 flex items-center justify-center`}></i>
                </div>
              </button>

              {/* Answer */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6 pl-24">
                  <div className="bg-[#E0F2F1] rounded-xl p-6 border-l-4 border-[#00A58E]">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#00A58E] rounded-full flex items-center justify-center text-white font-bold">
                        A
                      </div>
                      <p className="text-gray-700 leading-relaxed pt-1">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-white rounded-2xl p-8 shadow-md">
          <p className="text-lg text-gray-700 mb-4">
            その他のご質問やご不明点がございましたら、お気軽にお問い合わせください
          </p>
          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white px-10 py-4 rounded-full text-lg font-bold hover:shadow-xl transition-all shadow-lg whitespace-nowrap"
          >
            <i className="ri-question-answer-line mr-2"></i>
            お問い合わせはこちら
          </a>
        </div>
      </div>
    </section>
  );
}